import * as React from 'react';
import styled, { keyframes } from 'styled-components';

import { NavOption } from '../types';
import { BREAKPOINTS, COLORS } from '../styles';
import { VerticalTabs } from '../Tabs';
import { VerticalTabEntry } from '../Tabs/VerticalTabs';

const flowDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Menu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: ${COLORS.white};
  box-sizing: border-box;
  display: none;
  right: -8px;
  min-width: 300px;
  -webkit-animation: ${flowDown} 0.2s ease-in-out;
  animation: ${flowDown} 0.2s ease-in-out;
  position: absolute;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 100;

  top: 12px;
  ${BREAKPOINTS.SMALL} {
    top: 18px;
  }
  ${BREAKPOINTS.MEDIUM} {
    top: 24px;
  }

  .open & {
    display: block;
  }
`;

const MenuSection = styled.li`
  padding: 8px 0;
  position: relative;

  &:not(:first-of-type)::before {
    content: '';
    position: absolute;
    background: ${COLORS.neutral_100};
    height: 1px;
    top: 0;
    left: 16px;
    right: 16px;
  }

  .disabled-link {
    color: #21212150;
    pointer-events: none;
    key-events: none;
  }
`;

const MenuSectionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 2px;
`;

type Props = {
  clickMenuAction: () => void;
  navigationOptions: NavOption[];
};

const ExtendedMainMenu = ({ clickMenuAction, navigationOptions }: Props) => {
  const [entries, setEntries] = React.useState<VerticalTabEntry[]>([]);
  React.useEffect(() => {
    function handleKeyPress(e: any) {
      if (e.keyCode === 27) {
        clickMenuAction();
      }
    }

    document.addEventListener('keydown', handleKeyPress, false);
    return () => {
      document.removeEventListener('keydown', handleKeyPress, false);
    };
  }, [clickMenuAction]);

  React.useEffect(() => {
    const list = navigationOptions?.map(n => {
      return { requiredLine: n.label, to: n.to, disabled: n.disabled, onClick: n.onClick, exact: n.exact };
    }) as VerticalTabEntry[];
    setEntries(list);
  }, [navigationOptions]);

  return (
    <Menu onClick={clickMenuAction} role="menu" aria-labelledby="UserMenuButton">
      <MenuSection>
        <MenuSectionList>
          <VerticalTabs key="ExtendedMenu" entries={entries} size="medium" />
        </MenuSectionList>
      </MenuSection>
    </Menu>
  );
};

export default ExtendedMainMenu;
